import {
  DEFAULT_BUTTON_BORDER_COLOR,
  DEFAULT_BUTTON_FILL_COLOR,
  DEFAULT_BUTTON_TEXT_COLOR,
} from './factories';
import type {
  ButtonBlock,
  ButtonChrome,
  ButtonRowSlot,
  EmailSettings,
} from './types';

export type ButtonTheme = Pick<
  EmailSettings,
  'buttonFillColor' | 'buttonTextColor' | 'buttonBorderColor'
>;

export type ResolvedButtonChrome = Omit<
  ButtonChrome,
  'backgroundColor' | 'color' | 'borderColor'
> & {
  backgroundColor: string;
  color: string;
  borderColor: string;
};

export function resolveButtonFillColor(
  chrome: Pick<ButtonChrome, 'backgroundColor'>,
  theme: ButtonTheme,
): string {
  return chrome.backgroundColor ?? theme.buttonFillColor;
}

export function resolveButtonTextColor(
  chrome: Pick<ButtonChrome, 'color'>,
  theme: ButtonTheme,
): string {
  return chrome.color ?? theme.buttonTextColor;
}

export function resolveButtonBorderColor(
  chrome: Pick<ButtonChrome, 'borderColor' | 'backgroundColor'>,
  theme: ButtonTheme,
): string {
  return chrome.borderColor ?? theme.buttonBorderColor;
}

export function resolveButtonChrome(
  chrome: ButtonChrome,
  theme: ButtonTheme,
): ResolvedButtonChrome {
  return {
    ...chrome,
    backgroundColor: resolveButtonFillColor(chrome, theme),
    color: resolveButtonTextColor(chrome, theme),
    borderColor: resolveButtonBorderColor(chrome, theme),
  };
}

export function isButtonFillInherited(
  chrome: Pick<ButtonChrome, 'backgroundColor'>,
): boolean {
  return chrome.backgroundColor == null;
}

export function isButtonTextInherited(
  chrome: Pick<ButtonChrome, 'color'>,
): boolean {
  return chrome.color == null;
}

export function isButtonBorderInherited(
  chrome: Pick<ButtonChrome, 'borderColor'>,
): boolean {
  return chrome.borderColor == null;
}

function shouldClearButtonFill(
  color: string,
  settings: EmailSettings,
): boolean {
  return (
    color === DEFAULT_BUTTON_FILL_COLOR ||
    color === settings.buttonFillColor
  );
}

function shouldClearButtonText(color: string, settings: EmailSettings): boolean {
  return (
    color === DEFAULT_BUTTON_TEXT_COLOR ||
    color === settings.buttonTextColor
  );
}

function shouldClearButtonBorder(
  color: string,
  settings: EmailSettings,
): boolean {
  return (
    color === DEFAULT_BUTTON_BORDER_COLOR ||
    color === settings.buttonBorderColor
  );
}

export function normalizeButtonBlock(
  block: ButtonBlock,
  settings: EmailSettings,
): ButtonBlock {
  const next = {
    ...block,
    fullWidthMobile: block.fullWidthMobile ?? true,
  };

  if (
    next.backgroundColor != null &&
    shouldClearButtonFill(next.backgroundColor, settings)
  ) {
    delete next.backgroundColor;
  }

  if (
    next.color != null &&
    shouldClearButtonText(next.color, settings)
  ) {
    delete next.color;
  }

  if (
    next.borderColor != null &&
    shouldClearButtonBorder(next.borderColor, settings)
  ) {
    delete next.borderColor;
  }

  return next;
}

export function normalizeButtonRowSlot(
  slot: ButtonRowSlot,
  settings: EmailSettings,
): ButtonRowSlot {
  const next = { ...slot };

  if (
    next.backgroundColor != null &&
    shouldClearButtonFill(next.backgroundColor, settings)
  ) {
    delete next.backgroundColor;
  }

  if (
    next.color != null &&
    shouldClearButtonText(next.color, settings)
  ) {
    delete next.color;
  }

  if (
    next.borderColor != null &&
    shouldClearButtonBorder(next.borderColor, settings)
  ) {
    delete next.borderColor;
  }

  return next;
}
